import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, User } from "lucide-react";
import { CATEGORIES } from "../lib/categories";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "./BrandLogo";

const NAV_LINKS = [
  { to: "/shop", label: "Shop", testid: "nav-shop" },
  ...CATEGORIES.map((c) => ({
    to: `/category/${c.slug}`,
    label: c.short,
    testid: `nav-${c.slug}`,
  })),
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/research", label: "Research", testid: "nav-research" },
];

export default function Header() {
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();

  return (
    <>
      <div
        className="w-full bg-secondary text-secondary-foreground text-[11px] text-center py-2.5 px-4 uppercase tracking-[0.22em]"
        data-testid="ruo-banner"
      >
        Research Use Only · Not for Human Consumption
      </div>
      <div
        className="w-full bg-primary/10 text-secondary text-[10px] md:text-[11px] text-center py-2 px-4 uppercase tracking-[0.22em] border-b border-primary/20"
        data-testid="melbourne-banner"
      >
        Delivery &amp; Pickup — Melbourne only · Australia-wide delivery coming soon
      </div>
      <header
        className="sticky top-0 z-40 bg-background/95 backdrop-blur-xl border-b border-border/60"
        data-testid="site-header"
      >
        <div className="px-4 md:px-8 lg:px-12">
          {/* Top row: brand + account/cart */}
          <div className="flex items-center justify-between h-20 gap-4">
            <div className="flex items-center gap-5 shrink-0 min-w-0">
              <Link
                to="/"
                className="flex items-center shrink-0"
                data-testid="brand-home-link"
                aria-label="RegenX Wellness — home"
              >
                <BrandLogo variant="compact" iconSize={36} />
              </Link>
              <span
                aria-hidden="true"
                className="hidden md:block h-9 w-px bg-border"
              />
              <p
                className="hidden md:block font-serif italic text-base lg:text-lg text-primary whitespace-nowrap"
                data-testid="header-tagline"
              >
                Restore. Renew. Thrive.
              </p>
            </div>

            <div className="flex items-center gap-2 md:gap-5 shrink-0">
              <Link
                to={user ? "/account" : "/login"}
                className="hidden sm:flex items-center gap-2 text-secondary hover:text-primary transition-colors text-[11px] uppercase tracking-[0.18em]"
                data-testid="header-account-link"
              >
                <User className="h-4 w-4" />
                <span>{user ? user.full_name.split(" ")[0] : "Sign in"}</span>
              </Link>
              <button
                onClick={() => setDrawerOpen(true)}
                className="relative p-2 text-secondary hover:text-primary transition-colors"
                data-testid="header-cart-button"
                aria-label="Open cart"
              >
                <ShoppingBag className="h-5 w-5" />
                {count > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] min-w-[18px] h-[18px] flex items-center justify-center font-semibold"
                    data-testid="header-cart-count"
                  >
                    {count}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Always-visible inline nav menu */}
          <nav
            className="flex items-center justify-center gap-4 md:gap-8 lg:gap-12 border-t border-border/60 py-3 overflow-x-auto no-scrollbar"
            data-testid="primary-nav"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/shop"}
                data-testid={link.testid}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] md:text-xs uppercase tracking-[0.22em] font-medium transition-colors hover:text-primary ${
                    isActive ? "text-primary" : "text-secondary"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
    </>
  );
}
